'use client';

import { useApiKeys, useCreateApiKey, useRevokeApiKey } from '@hooks/useApiKeys';
import type { ApiKey, CreateApiKeyInput, CreatedApiKey } from '@interfaces/apiKey';
import { API_KEY_ERROR_CODES, hasErrorCode, isTheBoatDbApiError } from '@interfaces/apiKey';
import { type TheBoatDBConfigType, useConfig } from '@marine/configs';
import styles from '@styles/ApiKeys/ApiKeys.module.scss';
import { App, Button, Skeleton } from 'antd';
import Link from 'next/link';
import { useCallback, useMemo, useState } from 'react';
import { ApiKeysErrorState } from './ApiKeysErrorState';
import { ApiKeysTable } from './ApiKeysTable';
import { CreatedKeyModal } from './CreatedKeyModal';
import { CreateKeyModal } from './CreateKeyModal';
import { DisabledKeysNotice } from './DisabledKeysNotice';
import { keyLabel } from './keyLabel';
import { RevokeKeyModal } from './RevokeKeyModal';
import { UpgradePrompt } from './UpgradePrompt';

/** Turns a failed create/revoke into copy the dialog can show against the form. */
function describeMutationError(error: unknown): string | null {
  if (error === null || error === undefined) {
    return null;
  }

  if (!isTheBoatDbApiError(error)) {
    return 'Something went wrong. Check your connection and try again.';
  }

  switch (error.code) {
    case API_KEY_ERROR_CODES.VALIDATION_ERROR:
      return error.message;
    case API_KEY_ERROR_CODES.RATE_LIMITED:
      return 'You have made too many key management requests. Wait a moment and try again — your live integrations are unaffected.';
    case API_KEY_ERROR_CODES.UNAUTHORIZED:
      return 'Your session has expired. Sign in again and retry.';
    case API_KEY_ERROR_CODES.NOT_FOUND:
      return 'That key no longer exists. It may have been revoked in another tab.';
    case API_KEY_ERROR_CODES.KEY_LIMIT_REACHED:
      // Nothing about the request was wrong, so the message is the API's own —
      // it names the cap and the current count, which is what the user needs.
      return error.message;
    case API_KEY_ERROR_CODES.PRO_SUBSCRIPTION_REQUIRED:
      // Reachable even though the screen hides the create button without an
      // entitlement: a subscription can lapse while this page is open.
      return 'Creating API keys requires an active Pro subscription. Close this and use “Upgrade to Pro” to subscribe — any keys you already hold are unaffected.';
    case API_KEY_ERROR_CODES.SERVICE_UNAVAILABLE:
      return 'The key service is temporarily unreachable. Try again shortly.';
    default:
      return 'Something went wrong on our side. Try again, and contact support if it keeps happening.';
  }
}

const ApiKeysContent: React.FC = () => {
  const { message } = App.useApp();
  const { manageSubscriptionUrl } = useConfig<TheBoatDBConfigType>();

  const { data, isPending, isError, error, refetch, isRefetching } = useApiKeys();
  const createKey = useCreateApiKey();
  const revoke = useRevokeApiKey();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createdKey, setCreatedKey] = useState<CreatedApiKey | null>(null);
  const [keyPendingRevoke, setKeyPendingRevoke] = useState<ApiKey | null>(null);

  const keys = useMemo(() => data?.keys ?? [], [data]);

  const disabledCount = useMemo(
    () => keys.filter((key) => key.status === 'disabled' && key.disabledReason === 'entitlement_lapsed').length,
    [keys],
  );

  const openCreate = useCallback(() => {
    createKey.reset();
    setIsCreateOpen(true);
  }, [createKey]);

  const closeCreate = useCallback(() => {
    setIsCreateOpen(false);
    createKey.reset();
  }, [createKey]);

  const handleCreate = useCallback(
    (input: CreateApiKeyInput) => {
      createKey.mutate(input, {
        onSuccess: (created) => {
          setIsCreateOpen(false);
          setCreatedKey(created);
          message.success(`“${created.name}” was created.`);
        },
        onError: (mutationError) => {
          // Entitlement can lapse between loading this screen and submitting it.
          // Refetching swaps the page behind the dialog for the upgrade prompt —
          // but the dialog itself stays open, carrying the reason. Closing it
          // here (which is what this used to do) turned a refused create into a
          // click that appeared to do nothing at all.
          if (hasErrorCode(mutationError, API_KEY_ERROR_CODES.PRO_SUBSCRIPTION_REQUIRED)) {
            void refetch();
          }
        },
      });
    },
    [createKey, refetch, message.success],
  );

  const dismissCreatedKey = useCallback(() => {
    // Dropping the reference is what makes "shown once" true: nothing else in
    // this tree ever held the plaintext value.
    setCreatedKey(null);
  }, []);

  const handleRevoke = useCallback(
    (apiKey: ApiKey) => {
      revoke.mutate(apiKey.id, {
        onSuccess: () => {
          setKeyPendingRevoke(null);
          message.success(`“${keyLabel(apiKey)}” was revoked.`);
        },
      });
    },
    [revoke, message.success],
  );

  const openRevoke = useCallback(
    (apiKey: ApiKey) => {
      revoke.reset();
      setKeyPendingRevoke(apiKey);
    },
    [revoke],
  );

  const closeRevoke = useCallback(() => {
    setKeyPendingRevoke(null);
    revoke.reset();
  }, [revoke]);

  // The API's own answer, from the same entitlement check that decides `POST`.
  // Absent until the list has replied, and false rather than unknown after it
  // has — so nothing here offers creation on an assumption.
  const canCreate = data?.canCreateKeys === true;

  // Listing is not gated on Pro, so a lapsed subscriber reaches this screen with
  // a perfectly good list. The refused *list* is still handled, for the older
  // deployments that answered 403 to it.
  const listRefusedForPro = isError && hasErrorCode(error, API_KEY_ERROR_CODES.PRO_SUBSCRIPTION_REQUIRED);
  const needsUpgrade = listRefusedForPro || (!isPending && !isError && !canCreate);

  // One explanation, not two: `DisabledKeysNotice` already says the subscription
  // lapsed and offers the same link, so the prompt would only repeat it.
  const showUpgradePrompt = needsUpgrade && disabledCount === 0;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.header__title}>API keys</h1>
          <p className={styles.header__subtitle}>
            Keys authenticate your integrations against TheBoatDB public API. Use one key per integration, so revoking
            one does not take down the others.
          </p>
        </div>

        <div className={styles.header__actions}>
          <Button href="/docs/api" variant="outlined">
            API documentation
          </Button>
          {canCreate && (
            <Button color="primary" variant="solid" onClick={openCreate}>
              Create key
            </Button>
          )}
          {/* The upgrade path takes the create button's place rather than sitting
              beside it: the two are mutually exclusive by construction, and a
              disabled "Create key" would say what is missing but not how to get
              it. */}
          {needsUpgrade && manageSubscriptionUrl !== undefined && (
            <Button color="primary" variant="solid" style={{ color: 'white' }} href={manageSubscriptionUrl}>
              Upgrade to Pro
            </Button>
          )}
        </div>
      </header>

      <div className={styles.body}>
        {isPending && (
          <div className={`${styles.panel} ${styles['panel--padded']}`}>
            <Skeleton active paragraph={{ rows: 4 }} />
          </div>
        )}

        {showUpgradePrompt && <UpgradePrompt hasExistingKeys={keys.length > 0} />}

        {isError && !listRefusedForPro && (
          <ApiKeysErrorState error={error} onRetry={() => void refetch()} isRetrying={isRefetching} />
        )}

        {!isPending && !isError && (
          <>
            {disabledCount > 0 && <DisabledKeysNotice disabledCount={disabledCount} />}

            {keys.length > 0 && (
              <div className={styles.panel}>
                <ApiKeysTable keys={keys} onRevoke={openRevoke} isRefreshing={isRefetching} />
              </div>
            )}

            {/* Only for someone who could act on it. Without Pro the upgrade
                prompt above is already the whole answer, and an empty card under
                it would repeat the emptiness with a button that cannot work. */}
            {keys.length === 0 && canCreate && (
              <div className={`${styles.panel} ${styles.empty}`}>
                <p className={styles.empty__title}>No API keys yet</p>
                <p className={styles.empty__text}>
                  Create a key to start making requests. The key is shown once, when you create it.
                </p>
                <Button color="primary" variant="solid" onClick={openCreate}>
                  Create your first key
                </Button>
              </div>
            )}

            <p className={styles.footnote}>
              New to the API? Read the <Link href="/docs/api">public API documentation</Link> for endpoints, pagination
              and rate limits, or the <Link href="/docs/api/authentication">authentication guide</Link> for how to send
              a key safely.
            </p>
          </>
        )}
      </div>

      <CreateKeyModal
        open={isCreateOpen}
        isSubmitting={createKey.isPending}
        errorMessage={describeMutationError(createKey.error)}
        onSubmit={handleCreate}
        onCancel={closeCreate}
      />

      <CreatedKeyModal createdKey={createdKey} onDismiss={dismissCreatedKey} />

      <RevokeKeyModal
        apiKey={keyPendingRevoke}
        isRevoking={revoke.isPending}
        errorMessage={describeMutationError(revoke.error)}
        onConfirm={handleRevoke}
        onCancel={closeRevoke}
      />
    </div>
  );
};

/**
 * `App` supplies the message context antd's static methods cannot reach — the
 * app's `Theme` provider wraps `ConfigProvider` only.
 */
const ApiKeysManager: React.FC = () => (
  <App component={false}>
    <ApiKeysContent />
  </App>
);

export { ApiKeysManager };
